"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ClipboardList,
  Loader2,
  Mail,
  MessageCircle,
  MessagesSquare,
  Mic,
  Phone,
  Sparkles,
  Video,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api } from "~/trpc/react";
import { Button } from "~/components/ui/button";
import { SiteGlueCtaButton } from "~/components/siteglue/siteglue-cta";
import { marketingAppDashboardUrl } from "~/lib/auth-paths";
import { SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";
import { ArrowRight } from "lucide-react";
import {
  buildIndustryLiveDemoWidgetSrc,
  INDUSTRY_LIVE_DEMO_IFRAME_CLASS,
  INDUSTRY_LIVE_DEMO_PLACEHOLDER_MIN_H,
  INDUSTRY_LIVE_DEMO_TABS,
  isIndustryLiveDemoTabEnabled,
  type IndustryLiveDemoTabKey,
} from "~/lib/siteglue/industry-live-demo-channels";
import { trackSiteGlueEvent } from "~/lib/siteglue/cta-config";
import type { LiveDemoSectionConfig } from "~/components/siteglue/roles/shared/types";
import type { SidebarWidgetTab } from "~/app/components/landing-agent-sidebar";
import { cn } from "~/lib/utils";

const TAB_ICONS: Record<IndustryLiveDemoTabKey, LucideIcon> = {
  aiChat: MessageCircle,
  sms: MessagesSquare,
  email: Mail,
  contact: ClipboardList,
  talk: Mic,
  call: Phone,
  videoCall: Video,
  welcomeVideo: Video,
  whatsapp: MessageCircle,
  messenger: MessagesSquare,
};

function getOrCreateDemoSessionId(projectId: string): string | null {
  if (typeof window === "undefined") return null;
  const key = `ez-gpt-industry-demo-${projectId}`;
  const stored = sessionStorage.getItem(key)?.trim();
  if (stored) return stored;
  const id = crypto.randomUUID();
  sessionStorage.setItem(key, id);
  return id;
}

function DisabledChannelPlaceholder({
  label,
  message,
}: {
  label: string;
  message: string;
}) {
  return (
    <div
      className={`flex ${INDUSTRY_LIVE_DEMO_PLACEHOLDER_MIN_H} flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-8 py-12 text-center`}
      data-testid="industry-live-demo-disabled"
    >
      <Sparkles className="mb-4 h-10 w-10 text-slate-300" aria-hidden />
      <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {label} not available
      </p>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600">{message}</p>
    </div>
  );
}

function orderedTabs(tabOrder?: IndustryLiveDemoTabKey[]) {
  if (!tabOrder?.length) return INDUSTRY_LIVE_DEMO_TABS;
  const byKey = new Map(INDUSTRY_LIVE_DEMO_TABS.map((tab) => [tab.key, tab]));
  const ordered = tabOrder
    .map((key) => byKey.get(key))
    .filter((tab): tab is (typeof INDUSTRY_LIVE_DEMO_TABS)[number] => Boolean(tab));
  for (const tab of INDUSTRY_LIVE_DEMO_TABS) {
    if (!ordered.some((t) => t.key === tab.key)) ordered.push(tab);
  }
  return ordered;
}

export function IndustryLiveDemoSection({ config }: { config: LiveDemoSectionConfig }) {
  const tabs = useMemo(() => orderedTabs(config.tabOrder), [config.tabOrder]);
  const [activeTab, setActiveTab] = useState<IndustryLiveDemoTabKey>(
    config.initialTab ?? tabs[0]?.key ?? "aiChat",
  );
  const [sessionId] = useState(() => getOrCreateDemoSessionId(config.projectId));
  const viewTracked = useRef(false);
  const sectionRef = useRef<HTMLElement | null>(null);

  const projectQuery = api.project.publicGetProjectById.useQuery({
    id: config.projectId,
  });
  const { data: phoneAgents } = api.vapiAssistant.getAgentsForUser.useQuery({
    projectId: config.projectId,
  });

  const channels = projectQuery.data?.visualSettings?.channels as
    | Record<string, boolean | undefined>
    | undefined;
  const phoneAgent = phoneAgents?.[0] ?? null;
  const notificationSettings = projectQuery.data?.notificationSettings ?? null;
  const visualFlags = {
    talkAnamVideoEnabled: projectQuery.data?.visualSettings?.talkAnamVideoEnabled,
    welcomeVideoEnabled: projectQuery.data?.visualSettings?.welcomeVideoEnabled,
  };
  const agentName = projectQuery.data?.aiAgent?.name?.trim();
  const agentTitle = projectQuery.data?.aiAgent?.jobTitle?.trim();
  const organizationName = projectQuery.data?.title?.trim();

  const tabEnabledMap = useMemo(() => {
    const map = new Map<IndustryLiveDemoTabKey, boolean>();
    for (const tab of tabs) {
      map.set(
        tab.key,
        isIndustryLiveDemoTabEnabled(
          tab.key,
          channels,
          phoneAgent,
          notificationSettings,
          visualFlags,
        ),
      );
    }
    return map;
  }, [
    channels,
    notificationSettings,
    phoneAgent,
    tabs,
    visualFlags.talkAnamVideoEnabled,
    visualFlags.welcomeVideoEnabled,
  ]);

  useEffect(() => {
    const preferred = config.initialTab;
    if (!preferred) return;
    if (tabEnabledMap.get(preferred)) {
      setActiveTab(preferred);
    }
  }, [config.initialTab, tabEnabledMap]);

  const isAiPhoneSystemDemo = config.testIdPrefix === "ai-phone-system";

  useEffect(() => {
    if (!isAiPhoneSystemDemo) return;
    const el = sectionRef.current;
    if (!el || viewTracked.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting) || viewTracked.current) return;
        viewTracked.current = true;
        trackSiteGlueEvent("ai_phone_system_live_demo_view", {
          page: "ai-phone-system",
          demo_project_id: config.projectId,
        });
        observer.disconnect();
      },
      { threshold: 0.35 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [config.projectId, isAiPhoneSystemDemo]);

  const activeTabDef = tabs.find((t) => t.key === activeTab) ?? tabs[0]!;
  const activeEnabled = tabEnabledMap.get(activeTab) === true;
  const helperCopy = config.channelHelperCopy?.[activeTab];

  const iframeSrc = useMemo(() => {
    if (!activeEnabled) return null;
    const tabKey = config.hideOuterChrome
      ? (config.initialTab ?? activeTab)
      : activeTab;
    const tabDef = tabs.find((t) => t.key === tabKey) ?? activeTabDef;
    return buildIndustryLiveDemoWidgetSrc(
      config.projectId,
      tabDef.widgetTab as SidebarWidgetTab,
      sessionId,
    );
  }, [
    activeEnabled,
    activeTab,
    activeTabDef,
    config.hideOuterChrome,
    config.initialTab,
    config.projectId,
    sessionId,
    tabs,
  ]);

  const demoTitle =
    agentName && organizationName
      ? `${agentName}${agentTitle ? `, ${agentTitle}` : ""} at ${organizationName}`
      : config.demoAgentLabel ?? "Live demo agent";

  const agentFirstName = agentName?.split(/\s+/)[0] ?? "Agent";
  const onboardHref = marketingAppDashboardUrl(
    `/agents/${config.projectId}/hire?plan=intern`,
  );
  const onboardLabel = config.onboardCtaLabel ?? `Onboard ${agentFirstName} for Free`;
  const showOnboardCta = Boolean(config.onboardCtaLabel || config.onOnboardCtaClick);
  const showIntro =
    Boolean(config.eyebrow?.trim()) ||
    Boolean(config.heading?.trim()) ||
    Boolean(config.lead?.trim()) ||
    Boolean(config.positioningHeading?.trim());

  return (
    <section
      ref={sectionRef}
      id="live-demo"
      data-testid={`section-${config.testIdPrefix}-live-demo`}
      className={cn(
        config.embedded
          ? "mt-12"
          : "border-t border-slate-200 bg-white py-16 sm:py-20",
      )}
    >
      <div
        className={cn(
          config.embedded ? "mx-auto w-full max-w-5xl" : "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8",
        )}
      >
        {showIntro ? (
          <div className="mx-auto max-w-3xl text-center">
            {config.eyebrow ? (
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#4f81bd]">
                {config.eyebrow}
              </p>
            ) : null}
            {config.heading ? (
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                {config.heading}
              </h2>
            ) : null}
            {config.lead ? (
              <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                {config.lead}
              </p>
            ) : null}
            {config.positioningHeading ? (
              <div className="mx-auto mt-8 max-w-2xl rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-left sm:text-center">
                <p className="text-base font-semibold text-slate-900">
                  {config.positioningHeading}
                </p>
                {config.positioningLead ? (
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {config.positioningLead}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <div
          className={cn(
            "overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm",
            showIntro && "mt-10",
          )}
        >
          {!config.hideOuterChrome ? (
            <>
              <div className="flex flex-col gap-4 border-b border-slate-200 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="min-w-0 text-left">
                  <p className="text-sm font-semibold text-slate-900">{demoTitle}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Select a channel below to preview the live demo experience.
                  </p>
                </div>
                {agentName || config.onboardCtaLabel ? (
                  config.onOnboardCtaClick ? (
                    <Button
                      type="button"
                      size="default"
                      className="shrink-0 gap-1.5 rounded-full px-5 text-white hover:opacity-95"
                      style={{ backgroundColor: SITEGLUE_COLORS.blue }}
                      data-testid={`button-${config.testIdPrefix}-onboard-demo-agent`}
                      onClick={() => {
                        if (isAiPhoneSystemDemo) {
                          trackSiteGlueEvent("ai_phone_system_demo_interaction", {
                            page: "ai-phone-system",
                            interaction: "onboard_cta",
                            demo_project_id: config.projectId,
                          });
                        }
                        config.onOnboardCtaClick?.();
                      }}
                    >
                      {onboardLabel}
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </Button>
                  ) : (
                    <SiteGlueCtaButton
                      action="signup"
                      location="industry_page"
                      variant="primary"
                      size="default"
                      className="shrink-0 rounded-full px-5"
                      label={onboardLabel}
                      href={onboardHref}
                      showArrow
                      testId={`button-${config.testIdPrefix}-onboard-demo-agent`}
                    />
                  )
                ) : null}
              </div>

              <div
                className="flex gap-1 overflow-x-auto border-b border-slate-200 bg-white px-2 py-2 sm:px-4"
                role="tablist"
                aria-label="Demo channels"
              >
                {tabs.map((tab) => {
                  const Icon = TAB_ICONS[tab.key];
                  const enabled = tabEnabledMap.get(tab.key) === true;
                  const selected = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      data-testid={`tab-${config.testIdPrefix}-demo-${tab.key}`}
                      onClick={() => {
                        setActiveTab(tab.key);
                        trackSiteGlueEvent("live_demo_tab_selected", {
                          demo_tab: tab.key,
                          demo_project_id: config.projectId,
                          industry_slug: config.testIdPrefix,
                        });
                        if (isAiPhoneSystemDemo) {
                          trackSiteGlueEvent("ai_phone_system_demo_channel_select", {
                            page: "ai-phone-system",
                            demo_tab: tab.key,
                            demo_project_id: config.projectId,
                          });
                          trackSiteGlueEvent("ai_phone_system_demo_interaction", {
                            page: "ai-phone-system",
                            interaction: "channel_select",
                            demo_tab: tab.key,
                            demo_project_id: config.projectId,
                          });
                        }
                      }}
                      className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors sm:px-4 ${
                        selected
                          ? "text-white shadow-sm"
                          : enabled
                            ? "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                            : "text-slate-400 hover:bg-slate-50"
                      }`}
                      style={
                        selected
                          ? { backgroundColor: SITEGLUE_COLORS.blue }
                          : undefined
                      }
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="whitespace-nowrap">{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {helperCopy ? (
                <div className="border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
                  <p className="text-sm font-semibold text-slate-900">{helperCopy.heading}</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">
                    {helperCopy.description}
                  </p>
                </div>
              ) : null}
            </>
          ) : showOnboardCta ? (
            <div className="flex justify-end border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
              {config.onOnboardCtaClick ? (
                <Button
                  type="button"
                  size="default"
                  className="shrink-0 gap-1.5 rounded-full px-5 text-white hover:opacity-95"
                  style={{ backgroundColor: SITEGLUE_COLORS.blue }}
                  data-testid={`button-${config.testIdPrefix}-onboard-demo-agent`}
                  onClick={() => {
                    if (isAiPhoneSystemDemo) {
                      trackSiteGlueEvent("ai_phone_system_demo_interaction", {
                        page: "ai-phone-system",
                        interaction: "onboard_cta",
                        demo_project_id: config.projectId,
                      });
                    }
                    config.onOnboardCtaClick?.();
                  }}
                >
                  {onboardLabel}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Button>
              ) : (
                <SiteGlueCtaButton
                  action="signup"
                  location="industry_page"
                  variant="primary"
                  size="default"
                  className="shrink-0 rounded-full px-5"
                  label={onboardLabel}
                  href={onboardHref}
                  showArrow
                  testId={`button-${config.testIdPrefix}-onboard-demo-agent`}
                />
              )}
            </div>
          ) : null}

          <div className="p-3 sm:p-4">
            {projectQuery.isLoading ? (
              <div
                className={`flex ${INDUSTRY_LIVE_DEMO_PLACEHOLDER_MIN_H} items-center justify-center rounded-xl border border-slate-200 bg-white`}
              >
                <Loader2 className="h-8 w-8 animate-spin text-[#4f81bd]" aria-label="Loading demo" />
              </div>
            ) : activeEnabled && iframeSrc ? (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-inner">
                <iframe
                  key={
                    config.hideOuterChrome
                      ? config.projectId
                      : `${config.projectId}-${activeTab}`
                  }
                  src={iframeSrc}
                  title={`${activeTabDef.label} live demo`}
                  className={INDUSTRY_LIVE_DEMO_IFRAME_CLASS}
                  allow="microphone; camera; autoplay; clipboard-write"
                />
              </div>
            ) : (
              <DisabledChannelPlaceholder
                label={activeTabDef.label}
                message={activeTabDef.disabledPlaceholder}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
